// Far enough for any closed stretch a date input would plausibly skip
const MAX_SKIPPED_DAYS = 366;

const shiftDate = (date: string, days: number) => {
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
};

/**
 * The next date the prev/next buttons land on, skipping disabled dates.
 *
 * Dates are "yyyy-MM-dd" with no time zone, so the arithmetic stays on UTC
 * fields and never slips a day in the browser's zone.
 *
 * @param minDate - Only limits stepping back, so a value already past
 *   `maxDate` can still step back into range (and the same the other way).
 * @returns null when there's no enabled date in that direction, or when
 *   `value` doesn't parse (e.g. "202-11-02" while the user is still typing).
 */
export const stepDate = (
  value: string,
  direction: 1 | -1,
  {
    minDate,
    maxDate,
    isDateDisabled,
  }: {
    minDate?: string;
    maxDate?: string;
    isDateDisabled?: (date: string) => boolean;
  },
): string | null => {
  // Runs during render, so toISOString() throwing on an invalid date would
  // take the whole form down
  if (Number.isNaN(Date.parse(`${value}T00:00:00Z`))) return null;
  for (let days = 1; days <= MAX_SKIPPED_DAYS; days++) {
    const date = shiftDate(value, direction * days);
    if (direction < 0 && minDate && date < minDate) return null;
    if (direction > 0 && maxDate && date > maxDate) return null;
    if (!isDateDisabled?.(date)) return date;
  }
  return null;
};
